import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChecklistItem, ChecklistSection } from '../../../../types';
import { Plus, Icon } from '../../../ui/Icon';

interface Props {
  /** The owner's suitcase, by section: the only thing a piece can be made of (ADR-0018). */
  sections: ChecklistSection[];
  /** The clothing section: picked when the input opens. */
  defaultSectionId: string | null;
  /** Items already on this day, so they aren't offered twice. */
  excludeIds: Set<string>;
  onPick: (itemId: string) => Promise<void>;
  /** A garment not in the suitcase yet: it goes into the picked section first, then onto the day. */
  onCreate: (text: string, sectionId: string | null) => Promise<void>;
  onClose: () => void;
}

type Option =
  | { kind: 'item'; item: ChecklistItem; sectionTitle: string }
  | { kind: 'new'; text: string };

const MAX_SUGGESTIONS = 8;

/**
 * Picks a piece of your suitcase, or writes a new one. Untyped, it lists the
 * picked section — clothing by default, so a toothbrush is not offered as
 * something to wear. Typed, it searches every section of yours, the picked one
 * first, so a jacket kept elsewhere is found instead of duplicated.
 */
export default function OutfitPieceInput({ sections, defaultSectionId, excludeIds, onPick, onCreate, onClose }: Props) {
  const { t } = useTranslation();
  const listId = useId();
  const [draft, setDraft] = useState('');
  const [highlight, setHighlight] = useState(-1);
  const [sectionId, setSectionId] = useState(defaultSectionId ?? sections[0]?.id ?? null);
  const query = draft.trim().toLowerCase();
  const picked = sections.find(sec => sec.id === sectionId) ?? null;

  const options = useMemo<Option[]>(() => {
    const available = (sec: ChecklistSection) =>
      sec.items.filter(i => !excludeIds.has(i.id)).map(item => ({ kind: 'item' as const, item, sectionTitle: sec.title }));
    if (!query) return picked ? available(picked).slice(0, MAX_SUGGESTIONS) : [];

    const rank = (o: { item: ChecklistItem }) =>
      (o.item.text.trim().toLowerCase() === query ? 0 : 2) + (o.item.sectionId === sectionId ? 0 : 1);
    const matching = sections
      .flatMap(available)
      .filter(o => o.item.text.toLowerCase().includes(query))
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, MAX_SUGGESTIONS);
    const hasExact = matching.some(o => o.item.text.trim().toLowerCase() === query);
    return hasExact ? matching : [...matching, { kind: 'new', text: draft.trim() }];
  }, [sections, picked, sectionId, excludeIds, query, draft]);

  async function choose(option: Option) {
    setDraft('');
    setHighlight(-1);
    if (option.kind === 'item') await onPick(option.item.id);
    else await onCreate(option.text, sectionId);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' && options.length > 0) {
      e.preventDefault();
      setHighlight(h => (h + 1) % options.length);
    } else if (e.key === 'ArrowUp' && options.length > 0) {
      e.preventDefault();
      setHighlight(h => (h <= 0 ? options.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = options[highlight];
      if (option) choose(option);
      else onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  return (
    <div className="relative mt-1.5">
      <input
        autoFocus
        role="combobox"
        aria-expanded={options.length > 0}
        aria-controls={listId}
        aria-activedescendant={highlight >= 0 ? `${listId}-${highlight}` : undefined}
        aria-autocomplete="list"
        value={draft}
        onChange={e => { setDraft(e.target.value); setHighlight(e.target.value.trim() ? 0 : -1); }}
        onKeyDown={onKeyDown}
        onBlur={onClose}
        placeholder={t('packing.outfits.piecePlaceholder')}
        aria-label={t('packing.outfits.piecePlaceholder')}
        className="w-full bg-transparent border-b border-teal-400 dark:border-teal-500 text-sm py-1 text-slate-800 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
      />
      <div className="absolute z-30 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
        {sections.length > 1 && (
          // mousedown + preventDefault throughout: a click would blur the input and close the list first.
          <div role="group" aria-label={t('packing.outfits.fromSection')} className="flex flex-wrap gap-1 px-2 py-2 border-b border-slate-100 dark:border-slate-700">
            {sections.map(sec => (
              <button
                key={sec.id}
                type="button"
                aria-pressed={sec.id === sectionId}
                onMouseDown={e => { e.preventDefault(); setSectionId(sec.id); }}
                className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors ${
                  sec.id === sectionId
                    ? 'bg-teal-600 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/25 dark:hover:text-teal-300'
                }`}
              >
                {sec.title}
              </button>
            ))}
          </div>
        )}
        {options.length > 0 ? (
          <ul id={listId} role="listbox" className="py-1 max-h-64 overflow-y-auto">
            {options.map((option, i) => (
              <li
                key={option.kind === 'item' ? option.item.id : 'new'}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === highlight}
                onMouseDown={e => { e.preventDefault(); choose(option); }}
                onMouseEnter={() => setHighlight(i)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm cursor-pointer ${
                  i === highlight ? 'bg-teal-50 dark:bg-teal-900/25' : ''
                } ${option.kind === 'new' ? 'text-teal-700 dark:text-teal-300' : 'text-slate-700 dark:text-slate-200'}`}
              >
                {option.kind === 'item' ? (
                  <>
                    <span className="truncate">{option.item.text}</span>
                    {query && option.item.sectionId !== sectionId && (
                      <span className="ml-auto shrink-0 text-xs text-slate-400 dark:text-slate-500">{option.sectionTitle}</span>
                    )}
                  </>
                ) : (
                  <>
                    <Icon as={Plus} size={12} />
                    {picked
                      ? t('packing.outfits.addNewTo', { text: option.text, section: picked.title })
                      : t('packing.outfits.addNew', { text: option.text })}
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-3 py-2 text-xs text-slate-400 dark:text-slate-500">
            {picked ? t('packing.outfits.sectionEmpty') : t('packing.outfits.suitcaseEmpty')}
          </p>
        )}
      </div>
    </div>
  );
}
